"use client"
import { useState } from "react"
import { useHydrated } from "@/hooks/useHydrated"
import { motion } from "motion/react"
import { ScrollReveal } from "@/components/scroll/ScrollReveal"
import Link from "next/link"
import Image from "next/image"
import { Footer } from "@/components/layout/Footer"
import { Breadcrumbs, KeyTakeaways, FaqSection } from "@/components/seo/AeoBlocks"
import type { WorkContent, WorkProject } from "./work-content"
import { HeroRings, DotGrid, FloatingDots, ScanLine, CornerAccents } from "@/components/ui/BackgroundDecor"

const BG = "#0B0F1C"
const ACCENT = "#C4541A"
const MUTED = "rgba(255,255,255,0.6)"
const BORDER = "rgba(255,255,255,0.08)"

function Eyebrow({ label }: { label: string }) {
  return (
    <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 32 }}>
      <motion.span
        initial={{ width: 0 }}
        whileInView={{ width: 32 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: [0.33, 1, 0.68, 1] }}
        style={{ height: 1, background: ACCENT, flexShrink: 0, display: "block" }}
      />
      <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: MUTED }}>{label}</span>
    </div>
  )
}

export function WorkClient({ content }: { content: WorkContent }) {
  const { projects, promoVideos, filters } = content
  const [active, setActive] = useState("All")
  const hydrated = useHydrated()
  const filtered = active === "All" ? projects : projects.filter(p => p.category === active)

  return (
    <main style={{ background: BG, color: "#fff", minHeight: "100vh" }}>

      {/* HERO */}
      <div style={{ position: "relative", overflow: "hidden" }}>
        <motion.div
          animate={{ opacity: [0.65, 1, 0.65] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          style={{ position: "absolute", inset: 0, pointerEvents: "none", background: `radial-gradient(ellipse 80% 65% at 84% 26%, rgba(196,84,26,0.40) 0%, transparent 64%)` }}
        />
        <HeroRings side="right" />
        <DotGrid />
        <FloatingDots />
        <ScanLine />
        <CornerAccents />
        <section style={{ padding: "clamp(100px, 18vw, 160px) clamp(20px, 4vw, 48px) 60px", maxWidth: 1400, margin: "0 auto", position: "relative", zIndex: 1 }}>
          <ScrollReveal effect="fade-up">
            <Eyebrow label={content.heroEyebrow} />
            <h1 style={{ fontSize: "clamp(48px, 8vw, 120px)", fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 0.88, marginBottom: 40 }}>
              {content.heroHeadingLine1}<br /><span style={{ color: ACCENT }}>{content.heroHeadingLine2}</span>
            </h1>
            <p style={{ fontSize: "clamp(16px, 1.4vw, 20px)", color: MUTED, maxWidth: 560, lineHeight: 1.65 }}>
              {content.heroIntro}
            </p>
          </ScrollReveal>

          {/* Filter tabs */}
          <ScrollReveal effect="fade-up" delay={0.1}>
            <div style={{ display: "flex", gap: 12, marginTop: 56, flexWrap: "wrap" }}>
              {filters.map(f => (
                <motion.button
                  key={f}
                  onClick={() => setActive(f)}
                  whileTap={{ scale: 0.96 }}
                  style={{
                    padding: "10px 28px",
                    borderRadius: 100,
                    border: `1px solid ${active === f ? ACCENT : BORDER}`,
                    background: active === f ? ACCENT : "transparent",
                    color: active === f ? "#fff" : MUTED,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    letterSpacing: "-0.01em",
                    transition: "all 0.2s ease",
                  }}
                >
                  {f}
                </motion.button>
              ))}
            </div>
          </ScrollReveal>
        </section>
      </div>

      {/* PROJECT GRID */}
      <section style={{ padding: "0 clamp(20px, 4vw, 48px) clamp(60px, 8vw, 100px)", maxWidth: 1400, margin: "0 auto" }}>
        {active === "All" ? (
          <>
            {[{ label: "Website Projects", cat: "Web" }, { label: "Branding & Identity", cat: "Brand" }].map(group => {
              const groupProjects = projects.filter(p => p.category === group.cat)
              return (
                <div key={group.cat} style={{ marginBottom: 64 }}>
                  <ScrollReveal effect="fade-right">
                    <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 32 }}>
                      <span style={{ width: 32, height: 1, background: ACCENT, flexShrink: 0 }} />
                      <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: MUTED }}>{group.label}</span>
                      <span style={{ flex: 1, height: 1, background: BORDER }} />
                    </div>
                  </ScrollReveal>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3" style={{ gap: 16 }}>
                    {groupProjects.map((p, i) => (
                      <motion.div
                        key={p.id}
                        initial={hydrated ? { opacity: 0, y: 24 } : false}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: i * 0.06 }}
                      >
                        <ProjectCard project={p} />
                      </motion.div>
                    ))}
                  </div>
                </div>
              )
            })}
          </>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3" style={{ gap: 16 }}>
            {filtered.map((p, i) => (
              <motion.div
                key={p.id}
                initial={hydrated ? { opacity: 0, y: 24 } : false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
              >
                <ProjectCard project={p} />
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* PROMO VIDEOS */}
      {(active === "All" || active === "Web") && (
        <section style={{ padding: "0 clamp(20px, 4vw, 48px) clamp(80px, 10vw, 120px)", maxWidth: 1400, margin: "0 auto" }}>
          <ScrollReveal effect="fade-up">
            <div style={{ marginBottom: 40 }}>
              <Eyebrow label={content.videosEyebrow} />
              <h2 style={{ fontSize: "clamp(28px, 3.5vw, 52px)", fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 0.92 }}>
                {content.videosHeadingLine1}<br /><span style={{ color: ACCENT }}>{content.videosHeadingLine2}</span>
              </h2>
            </div>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 20 }}>
            {promoVideos.map((v, i) => (
              <ScrollReveal key={v.id} effect="fade-up" delay={i * 0.08}>
                <VideoCard videoId={v.id} thumb={v.thumb} />
              </ScrollReveal>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <div style={{ borderTop: `1px solid ${BORDER}`, padding: "clamp(60px, 8vw, 100px) clamp(20px, 4vw, 48px)" }}>
        <ScrollReveal effect="fade-up">
          <div style={{ maxWidth: 1400, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 40 }}>
            <div>
              <h2 style={{ fontSize: "clamp(32px, 4vw, 60px)", fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 0.92, marginBottom: 16 }}>
                {content.cta.headingLine1}<br /><span style={{ color: ACCENT }}>{content.cta.headingLine2}</span>
              </h2>
              <p style={{ color: MUTED, fontSize: 16, maxWidth: 400, lineHeight: 1.65 }}>
                {content.cta.body}
              </p>
            </div>
            <Link href={content.cta.primaryHref}>
              <motion.div
                whileHover={{ scale: 1.04, background: "#D4601F" }}
                whileTap={{ scale: 0.97 }}
                style={{ padding: "18px 48px", background: ACCENT, color: "#fff", borderRadius: 100, fontWeight: 700, fontSize: 16, letterSpacing: "-0.01em", cursor: "pointer" }}
              >
                {content.cta.primaryLabel}
              </motion.div>
            </Link>
          </div>
        </ScrollReveal>
      </div>

      {/* AEO/GEO structure — see components/seo/AeoBlocks.tsx for why each block exists. */}
      <section style={{ padding: "0 clamp(20px, 4vw, 48px) 40px", maxWidth: 1400, margin: "0 auto" }}>
        <Breadcrumbs trail={[{ name: "Work", path: "/work" }]} />
        <KeyTakeaways items={content.takeaways} />
        <FaqSection faqs={content.aeoFaqs} />
      </section>

      <Footer />
    </main>
  )
}

function ProjectCard({ project }: { project: WorkProject }) {
  const [hovered, setHovered] = useState(false)

  const card = (
    <motion.div
      animate={{ scale: hovered ? 1.015 : 1 }}
      transition={{ type: "spring", stiffness: 300, damping: 28 }}
      style={{ borderRadius: 20, overflow: "hidden", background: "#111520", border: `1px solid ${BORDER}`, display: "flex", flexDirection: "column" }}
    >
      <div style={{ position: "relative", width: "100%", aspectRatio: "16/9", overflow: "hidden", background: "#0d1117" }}>
        <Image
          src={project.image}
          alt={project.client}
          fill
          style={{ objectFit: project.imageFit ?? "cover", objectPosition: project.imageFit === "contain" ? "center" : "top", transition: "transform 0.5s ease", transform: `scale(${hovered ? (project.imageScale ?? 1) * 1.04 : (project.imageScale ?? 1)})` }}
          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
        />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, transparent 50%, rgba(11,15,28,0.5) 100%)" }} />
        <div style={{ position: "absolute", inset: 0, background: "rgba(196,84,26,0.22)", mixBlendMode: "multiply" }} />
      </div>

      <div style={{ padding: "16px 20px 20px", display: "flex", flexDirection: "column", gap: 8, flexGrow: 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ display: "inline-block", padding: "4px 14px", borderRadius: 100, background: "rgba(255,255,255,0.06)", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED }}>{project.tag}</span>
          {(project.caseStudyUrl || project.externalUrl) && (
            <span style={{ fontSize: 12, color: ACCENT, fontWeight: 600, opacity: hovered ? 1 : 0, transition: "opacity 0.2s ease" }}>
              {project.caseStudyUrl ? "Case Study →" : "Visit Site →"}
            </span>
          )}
        </div>
        <p style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: MUTED, marginTop: 4 }}>{project.client}</p>
        <h3 style={{ fontSize: "clamp(14px, 1.1vw, 17px)", fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.25 }}>{project.title}</h3>
        <p style={{ fontSize: 14, color: "rgba(255,255,255,0.5)", display: "flex", alignItems: "flex-start", gap: 10 }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: ACCENT, flexShrink: 0, marginTop: 5, display: "inline-block" }} />
          {project.outcome}
        </p>
        {project.testimonial && (
          <div style={{ borderLeft: `2px solid ${ACCENT}`, paddingLeft: 12, marginTop: 4 }}>
            <p style={{ fontSize: 13, color: MUTED, fontStyle: "italic", lineHeight: 1.6 }}>
              &ldquo;{project.testimonial}&rdquo;
            </p>
            {/* Present only on a quote pulled from a review the client submitted
                themselves, so it is attributed to the person who wrote it. */}
            {project.testimonialBy && (
              <p style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.45)", marginTop: 6 }}>
                &mdash; {project.testimonialBy}
              </p>
            )}
          </div>
        )}
      </div>
    </motion.div>
  )

  if (project.caseStudyUrl) {
    return (
      <Link href={project.caseStudyUrl} style={{ display: "block", textDecoration: "none", color: "#fff" }}
        onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
        {card}
      </Link>
    )
  }
  if (project.externalUrl) {
    return (
      <a href={project.externalUrl} target="_blank" rel="noopener noreferrer" style={{ display: "block", textDecoration: "none", color: "#fff" }}
        onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
        {card}
      </a>
    )
  }
  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      {card}
    </div>
  )
}

function VideoCard({ videoId }: { videoId: string; thumb?: string }) {
  // NO JavaScript anywhere in this component, on purpose.
  //
  // History: this was a <div onClick> facade. On a device where the bundle dies
  // (a real iPhone 8), tapping a video did nothing. Making it an <a> meant the tap
  // left the site for YouTube instead of playing in place. A CSS checkbox toggle
  // played in place but could not stop the hidden iframes loading — 88 network
  // requests on every visit, measured, because loading="lazy" does not defer a
  // display:none iframe.
  //
  // srcdoc solves both: the iframe renders its own poster from our R2 bucket and
  // fetches nothing from YouTube. The <a> inside navigates THIS iframe to the real
  // embed when tapped, so it plays exactly where it sits. Works with the bundle
  // dead, with JS disabled entirely, on any browser back to Safari 6.
  const poster = `https://media.boldpiq.com/work/poster-${videoId}.jpg`
  const embed = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`
  const srcDoc = `<!doctype html><html><head><meta charset="utf-8">` +
    `<style>*{margin:0;padding:0;box-sizing:border-box}html,body{height:100%;overflow:hidden;background:#111520}` +
    `a{display:block;height:100%;position:relative;text-decoration:none}` +
    `img{width:100%;height:100%;object-fit:cover;display:block}` +
    `.s{position:absolute;inset:0;background:rgba(0,0,0,.35)}` +
    `.b{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);width:clamp(56px,15vw,72px);aspect-ratio:1;` +
    `border-radius:50%;background:linear-gradient(145deg,#D9631F 0%,#C4541A 60%,#A8440F 100%);` +
    `border:1px solid rgba(255,255,255,.22);box-shadow:0 4px 24px rgba(196,84,26,.5),inset 0 1px 0 rgba(255,255,255,.25);` +
    `display:flex;align-items:center;justify-content:center;transition:transform .2s ease}` +
    `a:hover .b{transform:translate(-50%,-50%) scale(1.1)}a:hover img{transform:scale(1.03)}` +
    `img{transition:transform .4s ease}</style></head><body>` +
    `<a href="${embed}" aria-label="Play promo video">` +
    `<img src="${poster}" alt="">` +
    `<span class="s"></span>` +
    `<span class="b"><svg width="38%" height="38%" viewBox="0 0 24 24" aria-hidden="true" ` +
    `style="display:block;margin-left:10%;filter:drop-shadow(0 1px 2px rgba(0,0,0,.35))">` +
    `<path d="M4 2.5 L21 12 L4 21.5 Z" fill="#fff" stroke="#fff" stroke-width="2.5" stroke-linejoin="round"/>` +
    `</svg></span></a></body></html>`

  return (
    <div style={{ borderRadius: 20, overflow: "hidden", border: `1px solid ${BORDER}`, background: "#111520" }}>
      <iframe
        width="100%"
        style={{ aspectRatio: "16/9", display: "block", border: "none" }}
        srcDoc={srcDoc}
        title="BoldPiq promo video"
        data-cmp-ab="off"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  )
}
